import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {BooleanCellDisplayMode} from 'schemas/tags/BooleanValueCellEditor';
import {assertNever} from 'util/Types';

// Keep in sync with CustomColumnDataType in benchling/notebook/structured_tables/models/structured_table_column.py
export enum CustomColumnDataType {
  TEXT = 'text',
  INTEGER = 'integer',
  FLOAT = 'float',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATETIME = 'datetime',
}

export function getCustomColumnDataTypeDisplayName(dataType: CustomColumnDataType): string {
  switch (dataType) {
    case CustomColumnDataType.TEXT:
      return 'Text';
    case CustomColumnDataType.INTEGER:
      return 'Integer';
    case CustomColumnDataType.FLOAT:
      return 'Decimal';
    case CustomColumnDataType.BOOLEAN:
      return 'Boolean';
    case CustomColumnDataType.DATE:
      return 'Date';
    case CustomColumnDataType.DATETIME:
      return 'Datetime';
    default:
      assertNever(dataType);
      throw new Error(`Unexpected custom column data type: ${dataType}`);
  }
}

export function customColumnDataTypeToEquivalentFieldType(dataType: CustomColumnDataType): FieldType {
  switch (dataType) {
    case CustomColumnDataType.TEXT:
      return FieldType.TEXT;
    case CustomColumnDataType.INTEGER:
      return FieldType.INTEGER;
    case CustomColumnDataType.FLOAT:
      return FieldType.FLOAT;
    case CustomColumnDataType.BOOLEAN:
      return FieldType.BOOLEAN;
    case CustomColumnDataType.DATE:
      return FieldType.DATE;
    case CustomColumnDataType.DATETIME:
      return FieldType.DATETIME;
    default:
      assertNever(dataType);
      throw new Error(`Unexpected custom column data type: ${dataType}`);
  }
}

/**
 * If any tenant specific data is added to the CustomColumnConfig, it needs to be migrated / excluded
 * appropriately from structured table Config Migration in the StructuredTableColumnExporter
 */
export interface BaseCustomColumnConfig {
  dataType: CustomColumnDataType;
}
export interface TextCustomColumnConfig extends BaseCustomColumnConfig {
  dataType: CustomColumnDataType.TEXT;
}
export interface IntegerCustomColumnConfig extends BaseCustomColumnConfig {
  dataType: CustomColumnDataType.INTEGER;
}
export interface FloatCustomColumnConfig extends BaseCustomColumnConfig {
  dataType: CustomColumnDataType.FLOAT;
}
export interface BooleanCustomColumnConfig extends BaseCustomColumnConfig {
  dataType: CustomColumnDataType.BOOLEAN;
  // This cannot currently be set in the UI
  displayMode?: BooleanCellDisplayMode;
}
export interface DateCustomColumnConfig extends BaseCustomColumnConfig {
  dataType: CustomColumnDataType.DATE;
}

export interface DatetimeCustomColumnConfig extends BaseCustomColumnConfig {
  dataType: CustomColumnDataType.DATETIME;
}

export type CustomColumnConfig =
  | TextCustomColumnConfig
  | IntegerCustomColumnConfig
  | FloatCustomColumnConfig
  | BooleanCustomColumnConfig
  | DateCustomColumnConfig
  | DatetimeCustomColumnConfig;
